// Global Instructions Rule Applied!

import React, { useState, useMemo, forwardRef } from 'react'
import { debounce } from 'lodash'
import { InputSize, FieldValue } from '../types.ts'
import Select from '../../Select.jsx'
import { apiRequest } from '../../../lib/apiRequest.js'
import { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'

/** A contact option may carry the contact's email and mobile beside the generic entry fields (callers' static options do). */
type ContactSelectEntry = SelectListEntry & { email?: string; mobile_phone?: string }

export interface ContactInputProps {
  value: FieldValue
  onChange: (value: FieldValue) => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  size?: InputSize
  placeholder?: string
  mode?: 'single' | 'multiple'
  options?: ContactSelectEntry[]
  companyId?: string | null
  allowAddNew?: boolean
  disabled?: boolean
  autoFocus?: boolean
}

export const ContactInput = forwardRef<any, ContactInputProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      size = 'small',
      placeholder,
      mode = 'single',
      options: staticOptions = [],
      companyId = null,
      allowAddNew = true,
      disabled,
      autoFocus
    },
    ref
  ) => {
    const [options, setOptions] = useState<ContactSelectEntry[]>([])
    const [contactMetadata, setContactMetadata] = useState<
      Record<string, { email?: string; mobile_phone?: string; title?: string; job_title?: string; data?: any }>
    >({})
    const [loading, setLoading] = useState(false)
    const [searchValue, setSearchValue] = useState('')

    // Extract initial value options from value prop so Select can display labels
    const initialValueOptions = useMemo<SelectListEntry[]>(() => {
      if (!value) return []

      const opts: SelectListEntry[] = []

      if (mode === 'multiple') {
        if (Array.isArray(value)) {
          value.forEach((v: any) => {
            if (typeof v === 'object' && v.value !== undefined && v.label) {
              const nameOnly = v.label
              const labelWithEmail = `${v.label}${v.email && v.email !== '' ? ` (${v.email.toLowerCase()})` : ' (No Email)'}`

              opts.push({
                label: labelWithEmail,
                value: v.value,
                name: nameOnly
              })
            }
          })
        }
      } else {
        if (typeof value === 'object' && value && (value as any).value !== undefined && (value as any).label) {
          const v = value as any
          const nameOnly = v.label
          const labelWithEmail = `${v.label}${v.email && v.email !== '' ? ` (${v.email.toLowerCase()})` : ' (No Email)'}`

          opts.push({
            label: labelWithEmail,
            value: v.value,
            name: nameOnly
          })
        }
      }

      return opts
    }, [value, mode])

    // Debounced search function - memoized with companyId dependency
    const debouncedSearch = useMemo(
      () =>
        debounce(async (searchTerm: string, companyFilterId: string | null) => {
          if (!searchTerm || searchTerm.length < 2) {
            setOptions([])
            return
          }

          setLoading(true)
          try {
            const params = new URLSearchParams({
              search: searchTerm
            })

            if (companyFilterId) {
              params.append('companyId', companyFilterId)
            }

            const response = await apiRequest(`/contacts/select?${params}`)

            if (response.success && Array.isArray(response.data)) {
              const contactOptions: SelectListEntry[] = []
              const metadata: Record<
                string,
                { email?: string; mobile_phone?: string; title?: string; job_title?: string; data?: any }
              > = {}

              response.data.forEach(
                (contact: {
                  label: string
                  value: string | number
                  email?: string
                  mobile_phone?: string
                  job_title?: string
                }) => {
                  const nameOnly = contact.label
                  const labelWithEmail = `${contact.label}${contact.email && contact.email !== '' ? ` (${contact.email.toLowerCase()})` : ' (No Email)'}`

                  contactOptions.push({
                    label: labelWithEmail,
                    value: contact.value,
                    name: nameOnly
                  })

                  metadata[String(contact.value)] = {
                    title: nameOnly,
                    email: contact.email || '',
                    mobile_phone: contact.mobile_phone || '',
                    // Job title at the filtered company (companyId searches)
                    job_title: contact.job_title || ''
                  }
                }
              )

              if (allowAddNew && contactOptions.length === 0 && searchTerm.trim()) {
                const searchTrimmed = searchTerm.trim()
                contactOptions.push({
                  label: `${searchTrimmed} (new)`,
                  value: 'new',
                  name: `${searchTrimmed} (new)`
                })
                metadata['new'] = {
                  title: `${searchTrimmed} (new)`,
                  email: '',
                  mobile_phone: '',
                  data: { originalSearch: searchTrimmed }
                }
              }

              setOptions(contactOptions)
              setContactMetadata(metadata)
            } else {
              if (allowAddNew) {
                const searchTrimmed = searchTerm.trim()
                setOptions([
                  {
                    label: `${searchTrimmed} (new)`,
                    value: 'new',
                    name: `${searchTrimmed} (new)`
                  }
                ])
                setContactMetadata({
                  new: {
                    title: `${searchTrimmed} (new)`,
                    email: '',
                    data: { originalSearch: searchTrimmed }
                  }
                })
              } else {
                setOptions([])
                setContactMetadata({})
              }
            }
          } catch (error) {
            console.error('Error searching contacts:', error)
            if (allowAddNew && searchTerm.trim()) {
              const searchTrimmed = searchTerm.trim()
              setOptions([
                {
                  label: `${searchTrimmed} (new)`,
                  value: 'new',
                  name: `${searchTrimmed} (new)`
                }
              ])
              setContactMetadata({
                new: {
                  title: `${searchTrimmed} (new)`,
                  email: '',
                  data: { originalSearch: searchTrimmed }
                }
              })
            } else {
              setOptions([])
              setContactMetadata({})
            }
          } finally {
            setLoading(false)
          }
        }, 300),
      [allowAddNew]
    )

    const handleSearch = (searchTerm: string) => {
      setSearchValue(searchTerm)
      debouncedSearch(searchTerm, companyId)
    }

    const handleChange = (selectedValue: any, option: any) => {
      if (!allowAddNew) {
        // When allowAddNew is false, we still need to transform UUIDs to contact objects
        // The option parameter contains the full option with label
        if (mode === 'multiple') {
          // selectedValue is array of UUIDs, option is array of option objects
          const transformedValues = Array.isArray(selectedValue)
            ? selectedValue.map((val: any, index: number) => {
                // Try to find the corresponding option
                const opt = Array.isArray(option) ? option[index] : null
                // Try to find in current options or staticOptions
                const foundOption =
                  options.find((o) => o.value === val) || staticOptions.find((o) => o.value === val) || opt

                // Check if value already has label (from existing value prop)
                const existingContact = Array.isArray(value) ? value.find((v: any) => v.value === val) : null

                return {
                  value: val,
                  label: existingContact?.label || foundOption?.name || foundOption?.label || val,
                  email: existingContact?.email || foundOption?.email || '',
                  mobile_phone: existingContact?.mobile_phone || foundOption?.mobile_phone || ''
                }
              })
            : []
          onChange(transformedValues)
        } else {
          // Single mode - selectedValue is UUID, option is option object
          const foundOption =
            option ||
            options.find((o) => o.value === selectedValue) ||
            staticOptions.find((o) => o.value === selectedValue)

          // Check if value already has label (from existing value prop)
          const existingContact = typeof value === 'object' && value && !Array.isArray(value) ? (value as any) : null

          // Search results keep email/phone in contactMetadata, not on the
          // option object — consult it or selections lose those fields.
          const metadata = contactMetadata[String(selectedValue)] || {}

          const transformedValue = {
            value: selectedValue,
            label: existingContact?.label || metadata.title || foundOption?.name || foundOption?.label || selectedValue,
            email: existingContact?.email || metadata.email || foundOption?.email || '',
            mobile_phone: existingContact?.mobile_phone || metadata.mobile_phone || foundOption?.mobile_phone || '',
            job_title: metadata.job_title || ''
          }
          onChange(transformedValue)
        }
        return
      }

      if (mode === 'multiple') {
        const transformedValues = selectedValue.map((val: any) => {
          // Find existing contact data from current value prop (preserves data on re-render)
          const existingContact = Array.isArray(value) ? value.find((v: any) => v.value === val) : null

          // Try to get metadata from search results
          const metadata = contactMetadata[String(val)] || {}

          // Try to find option from current options list
          const existingOption =
            options.find((opt) => opt.value === val) || staticOptions.find((opt) => opt.value === val)

          // Preserve existing contact data, fallback to metadata, then option, then use val
          return {
            label: existingContact?.label || metadata.title || existingOption?.name || val,
            value: val, // val should now be a UUID from getDisplayValue fix
            email: existingContact?.email || metadata.email || '',
            mobile_phone: existingContact?.mobile_phone || metadata.mobile_phone || '',
            // Preserve any additional data from existing contact
            ...(existingContact?.data ? { data: existingContact.data } : {})
          }
        })
        onChange(transformedValues)
      } else {
        // Find existing contact data from current value prop
        const existingContact = typeof value === 'object' && value && !Array.isArray(value) ? (value as any) : null

        // Check if contact ID changed - if so, don't use existing contact's email/mobile_phone
        const contactIdChanged = existingContact && existingContact.value !== selectedValue

        // Try to get metadata from search results
        const metadata = contactMetadata[String(selectedValue)] || {}

        // Try to find option from current options list
        const existingOption =
          options.find((opt) => opt.value === selectedValue) || staticOptions.find((opt) => opt.value === selectedValue)

        // Preserve existing contact data, fallback to metadata, then option
        // BUT: If contact ID changed, prioritize metadata/option over existingContact's email/mobile_phone
        const transformedValue = {
          label: existingContact?.label || metadata.title || existingOption?.name || selectedValue,
          value: selectedValue, // selectedValue should now be a UUID from getDisplayValue fix
          email: contactIdChanged
            ? metadata.email || existingOption?.email || ''
            : existingContact?.email || metadata.email || existingOption?.email || '',
          mobile_phone: contactIdChanged
            ? metadata.mobile_phone || existingOption?.mobile_phone || ''
            : existingContact?.mobile_phone || metadata.mobile_phone || existingOption?.mobile_phone || '',
          job_title: metadata.job_title || '',
          // Preserve any additional data from existing contact only if contact ID hasn't changed
          ...(existingContact?.data && !contactIdChanged ? { data: existingContact.data } : {})
        }
        onChange(transformedValue)
      }
    }

    const getDisplayValue = () => {
      if (!value) return undefined

      if (mode === 'multiple') {
        if (Array.isArray(value)) {
          return value.map((v: any) => {
            // Return the UUID value, not the display label
            // This ensures Select receives UUIDs and onChange receives UUIDs
            if (typeof v === 'object' && v.value !== undefined) {
              return v.value
            }
            // Fallback for string values (shouldn't happen, but be safe)
            return typeof v === 'string' ? v : v
          })
        }
        return []
      } else {
        // Return the UUID value, not the display label
        if (typeof value === 'object' && value && (value as any).value !== undefined) {
          return (value as any).value
        }
        // Fallback for string values
        return typeof value === 'object' && value ? (value as any).value : value
      }
    }

    // Merge search results with initial value options and static options
    // Priority: search results > initial value options > static options
    const displayOptions = useMemo(() => {
      if (options.length > 0) {
        // When search results exist, merge them with initial value options to ensure
        // selected values can still be displayed even if not in search results
        const searchValues = new Set(options.map((opt) => opt.value))
        const mergedInitialOptions = initialValueOptions.filter((opt) => !searchValues.has(opt.value))
        return [...options, ...mergedInitialOptions]
      }

      // When no search results, use initial value options if available, otherwise static options
      if (initialValueOptions.length > 0) {
        return initialValueOptions
      }

      return staticOptions
    }, [options, initialValueOptions, staticOptions])

    return (
      <Select
        ref={ref}
        mode={mode}
        value={allowAddNew ? getDisplayValue() : value}
        onChange={handleChange}
        size={size}
        style={{ width: '100%' }}
        autoFocus={autoFocus}
        placeholder={placeholder || 'Type to search...'}
        options={displayOptions}
        onSearch={handleSearch}
        loading={loading}
        showSearch={true}
        filterOption={false}
        notFoundContent={
          loading ? 'Loading...' : searchValue.length > 0 ? 'No contacts found' : 'Type to search contacts'
        }
        onKeyDown={onKeyDown}
        disabled={disabled}
        allowClear
        optionLabelProp='name'
      />
    )
  }
)

ContactInput.displayName = 'ContactInput'
